import React, { Component, ReactNode } from 'react';

import { FILLS, STROKES } from '../styles/tokens';
import { RefreshIcon } from './icons';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ maxWidth: '1024px', margin: '32px auto', padding: '0 16px' }}>
          <div
            className="alert alert-error"
            style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '24px' }}
          >
            <strong style={{ fontSize: '1.1rem', marginBottom: '8px' }}>
              Something went wrong
            </strong>
            <p style={{ marginBottom: '16px' }}>
              We encountered an error while loading this page. Please try refreshing.
            </p>
            {typeof process !== 'undefined' &&
              process.env?.NODE_ENV === 'development' &&
              this.state.error && (
                <p
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: FILLS.textMuted,
                    marginBottom: '16px',
                  }}
                >
                  {this.state.error.message}
                </p>
              )}
            <button
              className="btn btn-outline"
              onClick={this.handleReset}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <RefreshIcon size={18} />
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
